import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Gift, Users, Copy, Share2, CheckCircle2, Crown,
  ArrowLeft, Trophy, Medal, Award, Loader2,
  Twitter, Facebook, MessageCircle, Mail
} from 'lucide-react';

const ReferralsPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);
  
  // Apply referral code
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, leaderboardRes] = await Promise.all([
        api.get('/referrals/stats'),
        api.get('/referrals/leaderboard')
      ]);
      
      setStats(statsRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (stats?.referral_code) {
      navigator.clipboard.writeText(stats.referral_code);
      setCopied(true);
      toast.success('Código copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getReferralLink = () => {
    if (!stats?.referral_code) return '';
    return `${window.location.origin}/auth?ref=${stats.referral_code}`;
  };

  const shareOnPlatform = (platform) => {
    const link = getReferralLink();
    const text = `¡Usa mi código ${stats?.referral_code} y obtén acceso a NotaryOS, el guardián legal autónomo! 🛡️`;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + link)}`,
      email: `mailto:?subject=${encodeURIComponent('Te invito a NotaryOS')}&body=${encodeURIComponent(text + '\n\n' + link)}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const handleApplyCode = async () => {
    if (!applyCode.trim()) {
      toast.error('Ingresa un código de referido');
      return;
    }
    
    setApplying(true);
    try {
      await api.post('/referrals/apply', { referral_code: applyCode.trim() });
      toast.success('¡Código aplicado! Tu amigo recibirá 1 mes premium cuando te suscribas.');
      setApplyCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setApplying(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Trophy className="w-5 h-5 text-yellow-400" />;
      case 2: return <Medal className="w-5 h-5 text-slate-300" />;
      case 3: return <Award className="w-5 h-5 text-amber-600" />;
      default: return <span className="text-sm font-bold text-slate-400">#{rank}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-gradient pb-20">
      {/* Header */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Gift className="w-7 h-7 text-purple-400" />
            <div>
              <h1 className="text-xl font-bold">Programa de Referidos</h1>
              <p className="text-sm text-slate-400">Invita amigos y gana meses Premium gratis</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 mb-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold mb-4">
              Invita amigos, gana Premium
            </h2>
            <p className="text-lg text-white/80 mb-6">
              Por cada amigo que se suscriba a Premium usando tu código, 
              <span className="font-bold text-white"> recibirás 1 mes de Premium GRATIS</span>.
            </p>
            
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-3 bg-white/20 rounded-xl px-4 py-3">
                <Crown className="w-6 h-6 text-yellow-300" />
                <div>
                  <p className="text-sm text-white/60">Meses ganados</p>
                  <p className="text-2xl font-bold">{stats?.rewards_earned_months || 0}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 bg-white/20 rounded-xl px-4 py-3">
                <Users className="w-6 h-6 text-green-300" />
                <div>
                  <p className="text-sm text-white/60">Referidos exitosos</p>
                  <p className="text-2xl font-bold">{stats?.successful_referrals || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 [&>*]:min-w-0">
          {/* Referral Code Section */}
          <div className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">Tu Código de Referido</h3>
              
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-3">
                  <p className="text-2xl font-mono font-bold text-blue-400">
                    {stats?.referral_code || '---'}
                  </p>
                </div>
                <button
                  onClick={copyReferralCode}
                  className={`p-3 rounded-lg transition-colors ${
                    copied ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-500'
                  }`}
                >
                  {copied ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              
              <p className="text-sm text-slate-400 mb-4">
                Comparte tu código con amigos:
              </p>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => shareOnPlatform('whatsapp')}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp</span>
                </button>
                <button
                  onClick={() => shareOnPlatform('twitter')}
                  className="flex items-center space-x-2 px-4 py-2 bg-sky-500 hover:bg-sky-400 rounded-lg transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                  <span>Twitter</span>
                </button>
                <button
                  onClick={() => shareOnPlatform('facebook')}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                  <span>Facebook</span>
                </button>
                <button
                  onClick={() => shareOnPlatform('email')}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span>Email</span>
                </button>
              </div>
            </div>

            {/* Apply Code Section */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">¿Tienes un código de referido?</h3>
              <p className="text-sm text-slate-400 mb-4">
                Si un amigo te compartió su código, ingrésalo aquí para que reciba su recompensa cuando te suscribas.
              </p>
              
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={applyCode}
                  onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                  placeholder="REF-XXXXXXXX"
                  className="flex-1 px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg focus:border-blue-500 transition-colors font-mono uppercase"
                />
                <button
                  onClick={handleApplyCode}
                  disabled={applying}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg transition-colors"
                >
                  {applying ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Aplicar'}
                </button>
              </div>
            </div>

            {/* Recent Referrals */}
            {stats?.recent_referrals?.length > 0 && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Tus Referidos Recientes</h3>
                
                <div className="space-y-3">
                  {stats.recent_referrals.map((ref, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-600/20 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium">{ref.referee_username}</p>
                          <p className="text-xs text-slate-400">{ref.referee_email}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        ref.status === 'completed'
                          ? 'bg-green-500/20 text-green-300'
                          : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {ref.status === 'completed' ? '✓ Premium' : 'Pendiente'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Leaderboard Section */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-6 flex items-center space-x-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <span>Top Referidores</span>
            </h3>
            
            {leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <p className="text-slate-400">Sé el primero en el ranking</p>
                <p className="text-sm text-slate-500">¡Comparte tu código y gana!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((entry, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-4 rounded-xl ${
                      entry.rank <= 3
                        ? 'bg-gradient-to-r from-yellow-500/10 to-transparent border border-yellow-500/20'
                        : 'bg-slate-900/50'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 flex items-center justify-center">
                        {getRankIcon(entry.rank)}
                      </div>
                      <div>
                        <p className="font-medium">{entry.username}</p>
                        <p className="text-xs text-slate-400">
                          {entry.referrals} referidos
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-400">
                        {entry.rewards_months} meses
                      </p>
                      <p className="text-xs text-slate-400">ganados</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* How it works */}
            <div className="mt-8 pt-6 border-t border-slate-700">
              <h4 className="font-semibold mb-4">¿Cómo funciona?</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-400">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Comparte tu código</p>
                    <p className="text-sm text-slate-400">Envía tu código único a tus amigos</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-400">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Ellos se registran</p>
                    <p className="text-sm text-slate-400">Tu amigo crea una cuenta con tu código</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-400">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Se suscriben a Premium</p>
                    <p className="text-sm text-slate-400">Cuando paguen su primera suscripción</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-green-400">¡Tú recibes 1 mes Premium!</p>
                    <p className="text-sm text-slate-400">Automáticamente se agrega a tu cuenta</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReferralsPage;
